import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

export const DEMO_VERIFICATION_RECORDS = [
  {
    id: 1,
    student_name: "Amina Juma",
    partner_name: "Tanzania Tech Hub",
    placement_title: "Software Development Intern",
    start_date: "2026-01-15",
    end_date: "2026-04-15",
    total_hours: 320,
    status: "verified",
    verified_by: "Dr. Sarah Mushi",
    verified_at: "2026-04-16T10:00:00Z",
    skills: ["React", "TypeScript", "Node.js", "PostgreSQL"],
    assessment_rating: 4.5,
    certificate_id: "TPC-2026-0001",
  },
  {
    id: 2,
    student_name: "Amina Juma",
    partner_name: "Dar es Salaam University",
    placement_title: "Research Assistant",
    start_date: "2025-09-01",
    end_date: "2025-12-15",
    total_hours: 180,
    status: "verified",
    verified_by: "Prof. John Kimaro",
    verified_at: "2025-12-20T14:00:00Z",
    skills: ["Data Analysis", "Python", "Research Methods"],
    assessment_rating: 4.8,
    certificate_id: "TPC-2025-0089",
  },
  {
    id: 3,
    student_name: "Amina Juma",
    partner_name: "AzamPay Tanzania",
    placement_title: "Mobile Payments Intern",
    start_date: "2026-02-01",
    end_date: "2026-05-01",
    total_hours: 240,
    status: "pending",
    verified_by: null,
    verified_at: null,
    skills: ["Flutter", "REST APIs", "Firebase", "Agile"],
    assessment_rating: null,
    certificate_id: null,
  },
];

export function useVerificationRecords() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["verification-records", user?.id],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_VERIFICATION_RECORDS;
      const [certificates, workLogs] = await Promise.all([
        apiClient
          .get(`/certificates/user/${user?.id}`)
          .then((res) => unwrapApiArray<any>(res))
          .catch(() => []),
        apiClient
          .get("/students/me/work-logs")
          .then((res) => unwrapApiArray<any>(res))
          .catch(() => []),
      ]);

      const certificateRecords = certificates.map((cert: any) => ({
        id: `cert-${cert.id}`,
        student_name: cert.student_name,
        partner_name: cert.university || "TaskPart Connect",
        placement_title: cert.course || "Issued certificate",
        start_date: cert.issue_date || cert.created_at,
        end_date: cert.issue_date || cert.created_at,
        total_hours: cert.total_hours || 0,
        status: cert.status === "revoked" ? "revoked" : "verified",
        verified_by: "TaskPart Connect",
        verified_at: cert.issue_date || cert.created_at,
        skills: [],
        assessment_rating: null,
        certificate_id: cert.cert_number,
      }));

      const logRecords = workLogs.map((log: any) => ({
        id: `log-${log.id}`,
        student_name: log.student_name,
        partner_name: log.partner_name || log.placement_title || "Partner",
        placement_title: log.placement_title || log.title || "Work log",
        start_date: log.date || log.created_at,
        end_date: log.reviewed_at || log.updated_at || log.date,
        total_hours: Number(log.hours || 0),
        status: log.status === "approved" ? "verified" : log.status || "pending",
        verified_by: log.reviewed_by || null,
        verified_at: log.reviewed_at || null,
        skills: Array.isArray(log.skills) ? log.skills : [],
        assessment_rating: null,
        certificate_id: null,
      }));

      return [...certificateRecords, ...logRecords];
    },
    initialData: user?.isDemo ? DEMO_VERIFICATION_RECORDS : [],
    enabled: !!user?.id,
  });
}

export function useVerifyRecord() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (data: { record_id: number; verified_by: string; notes?: string }) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 800));
        return {
          success: true,
          record_id: data.record_id,
          verified_by: data.verified_by,
          verified_at: new Date().toISOString(),
        };
      }
      const res = await apiClient.post("/partner/verify-record", data);
      return unwrapApiObject(res);
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["verification-records", user?.id], (old: any[]) =>
          old?.map((r: any) =>
            Number(r.id) === Number(result.record_id)
              ? { ...r, status: "verified", verified_by: result.verified_by, verified_at: result.verified_at }
              : r
          )
        );
      }
    },
  });
}
